import type {
    ConfigTemplateCluster,
    ConfigTemplateDraft,
    ConfigTemplateFieldSchema,
    ConfigTemplateFieldSchemaResponse,
    ConfigTemplateGroup,
    ConfigTemplatePart,
} from '../../../types/configTemplate.types';
import { clusterCardTitle } from './fieldMeta';

type CopyBaseline = ConfigTemplateDraft['copyBaseline'];

export function isSettingChanged(
    path: string,
    included: boolean,
    value: unknown,
    baseline?: CopyBaseline
): boolean {
    if (!baseline) return false;
    const wasIncluded = baseline.included.includes(path);
    if (included !== wasIncluded) return true;
    if (!included) return false;
    return JSON.stringify(value) !== JSON.stringify(baseline.values[path]);
}

export function changedFieldPaths(draft: ConfigTemplateDraft): Set<string> {
    const changed = new Set<string>();
    const baseline = draft.copyBaseline;
    if (!baseline) return changed;
    const current = new Set(draft.included);
    const before = new Set(baseline.included);
    for (const path of new Set([...current, ...before])) {
        if (
            isSettingChanged(path, current.has(path), draft.values[path], baseline)
        ) {
            changed.add(path);
        }
    }
    return changed;
}

export function fieldMatchesQuery(
    field: ConfigTemplateFieldSchema,
    query: string,
    context?: { partId?: string; clusterTitle?: string | null }
): boolean {
    if (!query) return false;
    if (field.label.toLowerCase().includes(query)) return true;
    const cardTitle = clusterCardTitle(context?.partId, context?.clusterTitle);
    if (cardTitle && cardTitle.toLowerCase().includes(query)) return true;
    if (context?.clusterTitle && context.clusterTitle.toLowerCase().includes(query)) {
        return true;
    }
    return false;
}

export function partFields(part: ConfigTemplatePart): ConfigTemplateFieldSchema[] {
    return part.clusters.flatMap((cluster) => cluster.fields ?? []);
}

export function countPartMatches(part: ConfigTemplatePart, query: string): number {
    if (!query) return 0;
    let matches = 0;
    for (const cluster of part.clusters) {
        for (const field of cluster.fields ?? []) {
            if (
                fieldMatchesQuery(field, query, {
                    partId: cluster.partId ?? part.id,
                    clusterTitle: cluster.title,
                })
            ) {
                matches += 1;
            }
        }
    }
    return matches;
}

export function countGroupMatches(group: ConfigTemplateGroup, query: string): number {
    if (!query) return 0;
    return group.parts.reduce((sum, part) => sum + countPartMatches(part, query), 0);
}

export function bestMatchingPartId(
    schema: ConfigTemplateFieldSchemaResponse,
    query: string
): string | null {
    if (!query) return null;
    let best: { id: string; n: number } | null = null;
    for (const group of schema.groups) {
        for (const part of group.parts) {
            const n = countPartMatches(part, query);
            if (n && (!best || n > best.n)) best = { id: part.id, n };
        }
    }
    return best?.id ?? null;
}

export function clustersWithChangedFields(
    schema: ConfigTemplateFieldSchemaResponse,
    changed: Set<string>
): ConfigTemplateCluster[] {
    const clusters: ConfigTemplateCluster[] = [];
    for (const group of schema.groups) {
        for (const part of group.parts) {
            for (const cluster of part.clusters) {
                const fields = (cluster.fields ?? []).filter((field) => changed.has(field.path));
                if (fields.length === 0) continue;
                clusters.push({ ...cluster, fields, partId: part.id });
            }
        }
    }
    return clusters;
}

export function filterClusters(
    clusters: ConfigTemplateCluster[],
    predicate: (field: ConfigTemplateFieldSchema) => boolean
): ConfigTemplateCluster[] {
    return clusters
        .map((cluster) => ({
            ...cluster,
            fields: (cluster.fields ?? []).filter(predicate),
        }))
        .filter((cluster) => (cluster.fields ?? []).length > 0);
}
